import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { Card, CardBody, Button } from './BaseComponents';

interface EmailPreferences {
    all: boolean;
}

/**
 * EmailPreferencesForm - Form component for managing email communication preferences
 *
 * Features:
 * - Simple toggle for all email communications
 * - Save only when changes are made
 * - Loading states during API calls
 * - Success/error feedback
 * - Default preferences handling for MVP
 */
export const EmailPreferencesForm: React.FC = () => {
    const { currentUser } = useUser();
    const [preferences, setPreferences] = useState<EmailPreferences>({ all: true });
    const [originalPreferences, setOriginalPreferences] = useState<EmailPreferences>({ all: true });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Load user's current email preferences
    useEffect(() => {
        const loadPreferences = async () => {
            if (currentUser) {
                try {
                    // TODO: Replace with actual API call when backend is implemented
                    // const userPreferences = await getEmailPreferences();

                    // For MVP, default to all emails enabled
                    const defaultPreferences = { all: true };
                    setPreferences(defaultPreferences);
                    setOriginalPreferences(defaultPreferences);
                } catch (error) {
                    console.error('Failed to load email preferences:', error);
                    // Use defaults on error
                    const defaultPreferences = { all: true };
                    setPreferences(defaultPreferences);
                    setOriginalPreferences(defaultPreferences);
                }
            }
        };

        loadPreferences();
    }, [currentUser]);

    // Check if preferences have changed
    const hasChanges = preferences.all !== originalPreferences.all;

    const handleToggleChange = (checked: boolean) => {
        setPreferences({ all: checked });
        setMessage(null); // Clear any existing messages
    };

    const handleSave = async () => {
        if (!hasChanges) return;

        setIsLoading(true);
        setMessage(null);

        try {
            // TODO: Replace with actual API call when backend is implemented
            // await updateEmailPreferences(preferences);

            // Simulate API call for MVP
            await new Promise(resolve => setTimeout(resolve, 500));

            setOriginalPreferences(preferences);
            setMessage({
                type: 'success',
                text: 'Email preferences updated successfully!'
            });
        } catch (error: any) {
            setMessage({
                type: 'error',
                text: error.message || 'Failed to update email preferences. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setPreferences(originalPreferences);
        setMessage(null);
    };

    return (
        <Card noHover={true}>
            <CardBody>
                <div className="space-y-6">
                    {/* Description */}
                    <div>
                        <p className="text-sm text-neutral-600">
                            Manage your email communication preferences. You can opt out of non-essential emails
                            while still receiving important account and security notifications.
                        </p>
                    </div>

                    {/* Email Preference Toggle */}
                    <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-neutral-900">
                                Email Communications
                            </h4>
                            <p className="text-xs text-neutral-600 mt-1">
                                Receive educational content, tips, and promotional emails
                            </p>
                        </div>
                        <div className="ml-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={preferences.all}
                                    onChange={(e) => handleToggleChange(e.target.checked)}
                                    className="sr-only"
                                />
                                <div className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                                    preferences.all ? 'bg-primary-400' : 'bg-neutral-300'
                                }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                                        preferences.all ? 'translate-x-5' : 'translate-x-0'
                                    } mt-0.5 ml-0.5`} />
                                </div>
                                <span className="ml-3 text-sm text-neutral-700">
                                    {preferences.all ? 'Enabled' : 'Disabled'}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Important Notice */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-xs text-blue-700">
                                    <strong>Note:</strong> You'll always receive important account notifications,
                                    security alerts, and service updates regardless of this setting.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`p-3 rounded-lg text-sm ${
                            message.type === 'success'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                            {message.text}
                        </div>
                    )}

                    {/* Action Buttons */}
                    {hasChanges && (
                        <div className="flex gap-3 pt-4 border-t border-neutral-200">
                            <Button
                                onClick={handleSave}
                                disabled={isLoading}
                                variant="primary"
                                className="flex-1"
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button
                                onClick={handleReset}
                                disabled={isLoading}
                                variant="outline"
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                        </div>
                    )}

                    {/* Current Status Display */}
                    {!hasChanges && (
                        <div className="text-center py-2">
                            <p className="text-xs text-neutral-500">
                                Email preferences are up to date
                            </p>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};